import axios from "axios";
import { brandPalette } from "@/lib/brand";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The portal session is the HttpOnly `resident_token` cookie set by the server (iter475 — nothing
// in localStorage, so a script injected into the page can't lift it).
export const auth = () => ({ withCredentials: true });

export const pget = (path) => axios.get(`${API}${path}`, auth());
export const ppost = (path, body = {}) => axios.post(`${API}${path}`, body, auth());
export const pput = (path, body = {}) => axios.put(`${API}${path}`, body, auth());
export const pdel = (path) => axios.delete(`${API}${path}`, auth());

export const fmtDate = (iso, opts = { month: "short", day: "numeric" }) => {
  try { return new Date(`${iso}T12:00:00`).toLocaleDateString([], opts); } catch { return iso || ""; }
};
export const fmtStamp = (iso) => {
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; }
};
export const fmtTime = (hhmm) => {
  try {
    const [h, m] = String(hhmm).split(":").map(Number);
    const am = h < 12; const hr = h % 12 || 12;
    return `${hr}${m ? ":" + String(m).padStart(2, "0") : ""}${am ? "am" : "pm"}`;
  } catch { return hhmm || ""; }
};

export const STATUS = {
  open: { label: "Open", cls: "bg-amber-50 text-amber-700" },
  in_progress: { label: "In process", cls: "bg-blue-50 text-blue-700" },
  on_hold: { label: "On hold", cls: "bg-violet-50 text-violet-700" },
  resolved: { label: "Completed", cls: "bg-emerald-50 text-emerald-700" },
  closed: { label: "Closed", cls: "bg-sand-100 text-ink/50" },
};

// Resident-friendly labels for a dispatched field-service job (work-order status).
// Mirrors the backend RESIDENT_JOB_STATUS map — no scary ops words like "overdue".
export const JOB_STATUS = {
  reported: { label: "Reported", cls: "bg-sand-100 text-ink/70" },
  dispatched: { label: "Sent to the pro", cls: "bg-blue-50 text-blue-700" },
  no_response: { label: "Sent to the pro", cls: "bg-blue-50 text-blue-700" },
  scheduled: { label: "Accepted", cls: "bg-indigo-50 text-indigo-700" },
  overdue: { label: "In progress", cls: "bg-blue-50 text-blue-700" },
  enroute: { label: "On site", cls: "bg-violet-50 text-violet-700" },
  completed: { label: "Done", cls: "bg-emerald-50 text-emerald-700" },
  completed_unverified: { label: "Done", cls: "bg-emerald-50 text-emerald-700" },
  closed: { label: "Closed", cls: "bg-emerald-50 text-emerald-700" },
  cancelled: { label: "Cancelled", cls: "bg-sand-100 text-ink/50" },
};

// Community white-label helpers ----------------------------------------------
export const communityLogoUrl = (community) =>
  community?.has_logo && community?.id ? `${API}/public/tenant/${community.id}/logo` : "";

// Turn a single brand hex into a usable theme (accent + readable on-color + soft tint).
// Falls back to the default Ask Redline look when the community hasn't set a color.
export const brandTheme = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || "").trim());
  if (!m) {
    return { set: false, accent: "#D32F2F", accentSoft: "rgba(211,47,47,0.10)", onAccent: "#ffffff",
             heroBg: "#1a1614", heroText: "#ffffff", heroSub: "rgba(255,255,255,0.62)" };
  }
  const h = m[1];
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  // Same readable "500" shade the staff console uses, so text/icons match across surfaces.
  const accent = brandPalette(`#${h}`)[500];
  const onAccent = "#ffffff";
  return {
    set: true, accent, onAccent,
    accentSoft: `rgba(${r},${g},${b},0.10)`,
    accentBorder: `rgba(${r},${g},${b},0.22)`,
    // Hero: the accent is already darkened until white reads on it, so it carries white text.
    heroBg: accent,
    heroText: "#ffffff",
    heroSub: "rgba(255,255,255,0.66)",
  };
};


// The sign-in page has no session, so the portal leaves the community's identity behind for it —
// a resident who signs out (or whose session lapsed) still sees THEIR community, not generic Redline.
const BRAND_KEY = "askredline.portalBrand";
export const rememberPortalBrand = (community) => {
  if (!community?.id) return;
  try {
    localStorage.setItem(BRAND_KEY, JSON.stringify({
      tenant_id: community.id, community: community.name, slug: community.slug || "",
      brand_color: community.brand_color || "", has_logo: !!community.has_logo,
      font_css: community.font_css || "", font_heading: community.font_heading || "", font_body: community.font_body || "",
    }));
  } catch { /* storage unavailable — sign-in just falls back to the Redline look */ }
};
export const rememberedPortalBrand = () => {
  try { return JSON.parse(localStorage.getItem(BRAND_KEY) || "null"); } catch { return null; }
};
export const forgetPortalBrand = () => { try { localStorage.removeItem(BRAND_KEY); } catch { /* ignore */ } };
